/**
 * usePatientLookup - shared TanStack Query hook for GET /api/patients/lookup/{ssn}
 *
 * Looks a kennitala up in the national registry and fetches its consent
 * status. Used by usePatientSearch (session patient selection) and the
 * blood-test order sheet (locked/lookup patient display).
 */

import type { Patient } from '@/types/session';
import { useQuery } from '@tanstack/react-query';

const STALE_TIME_MS = 5 * 60 * 1000;

/** Ten digits for `DDMMYYNNNN` or `DDMMYY-NNNN` (surrounding space ignored); undefined for anything else. */
export const normalizeKennitala = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim() ?? '';
  return /^\d{6}-?\d{4}$/.test(trimmed) ? trimmed.replace('-', '') : undefined;
};

interface PatientLookupApiResponse {
  person?: { name?: string | null } | null;
  consent_status?: Patient['consentStatus'];
  fhir_patient_id?: string | null;
}

export interface PatientLookupResult {
  /** Registry name, or null when the kennitala was not found in Þjóðskrá. */
  name: string | null;
  consentStatus: NonNullable<Patient['consentStatus']>;
  fhirPatientId?: string | null;
}

/**
 * Looks up a patient by kennitala. Disabled until `value` normalizes to a valid
 * kennitala, so switching to an incomplete/different value naturally resets
 * the result (new query key, no stale data carried over).
 */
export const usePatientLookup = (value: string | undefined, options: { enabled?: boolean } = {}) => {
  const ssn = normalizeKennitala(value);
  return useQuery({
    queryKey: ['patients', 'lookup', ssn],
    queryFn: async ({ signal }): Promise<PatientLookupResult> => {
      const response = await fetch(`/api/patients/lookup/${ssn}`, {
        credentials: 'include',
        signal,
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch patient info: ${response.status}`);
      }
      const data: PatientLookupApiResponse = await response.json();
      return {
        name: data.person?.name ?? null,
        consentStatus: data.consent_status ?? 'error',
        fhirPatientId: data.fhir_patient_id,
      };
    },
    enabled: options.enabled !== false && ssn !== undefined,
    staleTime: STALE_TIME_MS,
  });
};
